/**
 * Stepper visuel commun à tous les funnels (Aides, Agri, Comparateur,
 * Diagnostic, Décret Tertiaire).
 *
 * Différences avec le Stepper UI générique :
 *  - Animations Framer Motion (gradient progress bar + tick)
 *  - Adapte le rendu mobile : barre de progression + label compact "X sur Y"
 *  - Mise en avant de l'étape courante avec un halo
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface FunnelStepperStep {
  id: string;
  label: string;
}

interface FunnelStepperProps {
  steps: FunnelStepperStep[];
  /** Index 0-based de l'étape courante. */
  current: number;
  className?: string;
}

export function FunnelStepper({ steps, current, className }: FunnelStepperProps) {
  const total = steps.length;
  const safeCurrent = Math.max(0, Math.min(current, total - 1));
  const progress = total > 1 ? safeCurrent / (total - 1) : 0;
  const currentLabel = steps[safeCurrent]?.label ?? '';

  return (
    <div
      role="progressbar"
      aria-valuenow={safeCurrent + 1}
      aria-valuemin={1}
      aria-valuemax={total}
      aria-label={`Étape ${safeCurrent + 1} sur ${total} — ${currentLabel}`}
      className={cn('w-full', className)}
    >
      {/* ── Mobile : barre fine + label compact ── */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500">
            Étape {safeCurrent + 1}<span className="mx-1.5 text-stone-300">/</span>{total}
          </p>
          <AnimatePresence mode="wait">
            <motion.p
              key={currentLabel}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: easeStripe }}
              className="min-w-0 truncate pl-3 text-[13px] font-semibold text-brand-700"
            >
              {currentLabel}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-200/70">
          <motion.div
            initial={false}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.4, ease: easeStripe }}
            className="h-full rounded-full bg-gradient-to-r from-brand-500 via-brand-400 to-accent-500"
          />
        </div>
      </div>

      {/* ── Desktop : pastilles + labels ── */}
      <div className="hidden sm:flex sm:items-center">
        {steps.map((step, i) => {
          const active = i === safeCurrent;
          const done = i < safeCurrent;
          return (
            <div
              key={step.id}
              className={cn('flex items-center', i > 0 && 'flex-1')}
            >
              {i > 0 && (
                <div className="relative h-px flex-1 overflow-hidden bg-stone-200">
                  <motion.div
                    initial={false}
                    animate={{ scaleX: done ? 1 : 0 }}
                    transition={{ duration: 0.4, ease: easeStripe }}
                    style={{ transformOrigin: 'left' }}
                    className="absolute inset-0 bg-gradient-to-r from-brand-400 via-brand-500 to-brand-400"
                  />
                </div>
              )}
              <div className="flex shrink-0 items-center gap-2">
                <motion.div
                  initial={false}
                  animate={{
                    scale: active ? 1.06 : 1,
                  }}
                  transition={{ duration: 0.25, ease: easeStripe }}
                  className={cn(
                    'relative flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold transition-colors duration-300',
                    active
                      ? 'bg-brand-600 text-white shadow-[0_0_0_4px_rgba(16,185,129,0.12)]'
                      : done
                        ? 'bg-brand-500 text-white'
                        : 'bg-stone-100 text-stone-400 ring-1 ring-inset ring-stone-200/70',
                  )}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {done ? (
                      <motion.span
                        key="check"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ duration: 0.18, ease: easeStripe }}
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                      </motion.span>
                    ) : (
                      <motion.span
                        key={`num-${i}`}
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ duration: 0.18, ease: easeStripe }}
                      >
                        {i + 1}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {active && (
                    <motion.span
                      aria-hidden
                      className="absolute inset-0 rounded-full bg-brand-400/30"
                      initial={{ scale: 1, opacity: 0.5 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      transition={{
                        duration: 1.8,
                        ease: easeStripe,
                        repeat: Infinity,
                      }}
                    />
                  )}
                </motion.div>
                <span
                  className={cn(
                    'hidden text-[11.5px] font-semibold leading-none transition-colors duration-300 md:block',
                    active
                      ? 'text-brand-700'
                      : done
                        ? 'text-stone-500'
                        : 'text-stone-400',
                  )}
                >
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
